import React, { useState, useEffect, useRef } from 'react';
import { Input, Dropdown, TextArea, Form, Button } from 'semantic-ui-react';
import _ from 'lodash';

// cronOptions param editörleri. Tipler live-api CronService getCronSchema ile aynı:
// state, duration (sn), enum, text, bool, lastFrom, tag, tags, group, labels, message, messages

const UNITS = [
	{ key: 'sn', value: 1, text: 'sn' },
	{ key: 'dk', value: 60, text: 'dk' },
	{ key: 'saat', value: 3600, text: 'saat' },
	{ key: 'gün', value: 86400, text: 'gün' },
];

function bestUnit(seconds) {
	let unit = UNITS[0].value;
	_.each(UNITS, (u) => {
		if (seconds && seconds % u.value === 0) unit = u.value;
	});
	return unit;
}

export function formatDuration(seconds) {
	if (!_.isNumber(seconds)) return '?';
	let unit = bestUnit(seconds);
	return seconds / unit + ' ' + _.find(UNITS, { value: unit }).text;
}

function DurationInput({ value, onChange }) {
	const [unit, setUnit] = useState(bestUnit(value));
	let shown = _.isNumber(value) ? value / unit : '';
	return (
		<Input
			type="number"
			min={0}
			value={shown}
			onChange={(e, { value: v }) => onChange(v === '' ? undefined : Math.round(parseFloat(v) * unit))}
			label={<Dropdown value={unit} options={UNITS} onChange={(e, { value: u }) => setUnit(u)} />}
			labelPosition="right"
			style={{ width: 180 }}
		/>
	);
}

function MessageInput({ value, onChange }) {
	// sadece metin mesajı formla düzenlenir; diğer tipler (resim vb.) JSON
	if (value && value.type && value.type !== 'text') return <JsonInput value={value} onChange={onChange} />;
	return (
		<Form>
			<TextArea
				rows={2}
				value={(value && value.text) || ''}
				placeholder="Mesaj metni"
				onChange={(e, { value: text }) => onChange(text ? _.assign({}, value, { type: 'text', text }) : undefined)}
			/>
		</Form>
	);
}

function MessagesInput({ value, onChange }) {
	let list = _.isArray(value) ? value : [];
	let set = (next) => onChange(next.length ? next : undefined);
	return (
		<div>
			{_.map(list, (msg, i) => (
				<div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
					<div style={{ flex: 1 }}>
						<MessageInput value={msg} onChange={(m) => set(_.filter(_.map(list, (x, j) => (j === i ? m : x)), Boolean))} />
					</div>
					<Button icon="trash" size="mini" basic onClick={() => set(_.filter(list, (x, j) => j !== i))} />
				</div>
			))}
			<Button size="mini" basic icon="plus" content="Mesaj ekle" onClick={() => onChange(list.concat([{ type: 'text', text: '' }]))} />
		</div>
	);
}

const toText = (v) => (v === undefined ? '' : JSON.stringify(v, null, 2));

export function JsonInput({ value, onChange, rows = 4 }) {
	const [text, setText] = useState(toText(value));
	const [error, setError] = useState(null);
	const emitted = useRef(value);
	// dışarıdan gelen değişiklik (geri al, ham JSON vb.) metne yansısın; kendi yazdığımız değer imleci bozmasın
	useEffect(() => {
		if (!_.isEqual(value, emitted.current)) {
			emitted.current = value;
			setText(toText(value));
			setError(null);
		}
	}, [value]);
	const emit = (v) => {
		emitted.current = v;
		onChange(v);
	};
	return (
		<Form>
			<TextArea
				rows={rows}
				value={text}
				style={{ fontFamily: 'monospace', fontSize: 12, borderColor: error ? '#db2828' : undefined }}
				onChange={(e, { value: t }) => {
					setText(t);
					if (!t.trim()) {
						setError(null);
						return emit(undefined);
					}
					try {
						emit(JSON.parse(t));
						setError(null);
					} catch (err) {
						setError(err.message);
					}
				}}
			/>
			{error ? <div style={{ color: '#db2828', fontSize: 12 }}>{error}</div> : null}
		</Form>
	);
}

function optionsWithValue(options, values) {
	// listede olmayan mevcut değerler de görünsün (silinmiş grup/etiket vb.)
	let extra = _.filter(_.castArray(values || []), (v) => v !== undefined && v !== '' && !_.find(options, { value: v }));
	return options.concat(_.map(extra, (v) => ({ key: 'x' + v, value: v, text: v + ' (listede yok)' })));
}

export function ParamField({ meta, value, onChange, ctx }) {
	switch (meta.type) {
		case 'duration':
			return <DurationInput value={value} onChange={onChange} />;
		case 'state': {
			let states = _.sortBy(ctx.schema.states, (s) => (meta.target ? !s.target : s.target));
			let options = _.map(states, (s) => ({
				key: s.key,
				value: s.key,
				text: s.label,
				description: s.key + (s.target !== !!meta.target ? (meta.target ? ' (koşul durumu)' : ' (hedef durumu)') : ''),
			}));
			if (_.isPlainObject(value)) return <JsonInput value={value} onChange={onChange} />;
			return <Dropdown selection search clearable value={value || ''} options={optionsWithValue(options, value)} onChange={(e, d) => onChange(d.value || undefined)} />;
		}
		case 'enum':
			return (
				<Dropdown
					selection
					value={value === undefined ? '' : value}
					options={optionsWithValue(_.map(meta.options, (o) => ({ key: o.value, value: o.value, text: o.label })), value)}
					onChange={(e, d) => onChange(d.value)}
				/>
			);
		case 'bool':
			return (
				<Dropdown
					selection
					value={value === undefined ? 'u' : value ? 't' : 'f'}
					options={[
						{ key: 'u', value: 'u', text: '—' },
						{ key: 't', value: 't', text: 'Evet' },
						{ key: 'f', value: 'f', text: 'Hayır' },
					]}
					onChange={(e, d) => onChange(d.value === 'u' ? undefined : d.value === 't')}
				/>
			);
		case 'lastFrom':
			return (
				<Dropdown
					selection
					value={value === undefined ? 'u' : value ? 't' : 'f'}
					options={[
						{ key: 'u', value: 'u', text: 'Fark etmez' },
						{ key: 't', value: 't', text: 'Son mesaj kullanıcıdan' },
						{ key: 'f', value: 'f', text: 'Son mesaj kullanıcıdan değil' },
					]}
					onChange={(e, d) => onChange(d.value === 'u' ? undefined : d.value === 't')}
				/>
			);
		case 'group':
			return (
				<Dropdown
					selection
					search
					clearable
					allowAdditions
					value={value || ''}
					options={optionsWithValue(ctx.groups, value)}
					onChange={(e, d) => onChange(d.value || undefined)}
				/>
			);
		case 'tag':
			return (
				<Dropdown
					selection
					search
					clearable
					allowAdditions
					value={value || ''}
					options={optionsWithValue(ctx.tags, value)}
					onChange={(e, d) => onChange(d.value || undefined)}
				/>
			);
		case 'tags': {
			let list = _.compact(_.castArray(value || []));
			return (
				<Dropdown
					selection
					multiple
					search
					allowAdditions
					value={list}
					options={optionsWithValue(ctx.tags, list)}
					onChange={(e, d) => onChange(d.value.length ? d.value : undefined)}
				/>
			);
		}
		case 'labels': {
			// live-api virgüllü metin bekliyor
			let list = _.compact(String(value || '').split(','));
			return (
				<Dropdown
					selection
					multiple
					search
					allowAdditions
					value={list}
					options={optionsWithValue(ctx.tags, list)}
					onChange={(e, d) => onChange(d.value.length ? d.value.join(',') : undefined)}
				/>
			);
		}
		case 'message':
			return <MessageInput value={value} onChange={onChange} />;
		case 'messages':
			return <MessagesInput value={value} onChange={onChange} />;
		default:
			return <Input value={value === undefined ? '' : value} placeholder={meta.default} onChange={(e, d) => onChange(d.value === '' ? undefined : d.value)} />;
	}
}
